import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getEntriesByContentTypeAndProduct } from 'api/contentful';

import CopyButton from 'components/CopyButton';
import { H1, H4 } from 'components/Typography';

import Accordion from 'components/Accordion';
import {
  policiesCommunityRH,
  policiesNIC,
  policiesIndividualsRH,
  policiesNH,
  archiveNI,
  archiveNIC,
  archiveNH,
  archiveCommunityRH,
  archiveAffinityRH,
  archiveIndividualsRH
} from './content';

import { PoliciesContainer, Policy, PolicyName, Product } from './styled';

const PolicyList = ({ policies }) =>
  policies.map((policy, i) => {
    const policyLink = policy.link.startsWith('https://')
      ? policy.link
      : `${window.location.origin}${policy.link}`;
    return (
      <Policy key={i}>
        <CopyButton value={policyLink} />
        <PolicyName href={policyLink}>{policy.name}</PolicyName>
      </Policy>
    );
  });

PolicyList.propTypes = {
  policies: PropTypes.array.isRequired
};

const Policies = () => {
  const [carrierPolicies, setCarrierPolicies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCarrierPolicies = async () => {
    const data = await getEntriesByContentTypeAndProduct('policy', 'nomad-insurance', 5);
    setCarrierPolicies(data);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchCarrierPolicies();
  }, []);

  const [firstCarrierPolicy, firstCarrierPolicyPdf] =
    carrierPolicies.length > 0
      ? carrierPolicies.map((policy) => {
          const effectiveDate = new Date(policy.fields.effectiveDate).toLocaleDateString(
            'en-US',
            {
              month: '2-digit',
              year: 'numeric'
            }
          );
          return [
            {
              name: `Carrier - Dynamic (${effectiveDate})`,
              link: `/nomad-insurance/policy`
            },
            {
              name: `Carrier - PDF (12/2024)`, // static for this PDF, it's an exception
              link: `https://${policy.fields.policyDocumentPdf.fields.file.url}`
            }
          ];
        })[0]
      : [
          { name: '', link: '' },
          { name: '', link: '' }
        ];

  return (
    <PoliciesContainer bg="light" isLoading={isLoading}>
      <H1>All policy versions</H1>
      <Product>
        <H4>Nomad Insurance Essential policy versions</H4>
        <PolicyList policies={[firstCarrierPolicy, firstCarrierPolicyPdf]} />
      </Product>

      <Product>
        <H4>Nomad Insurance Complete policy versions</H4>
        <PolicyList policies={policiesNIC} />
      </Product>

      <Product>
        <H4>Remote health community policy versions</H4>
        <PolicyList policies={policiesCommunityRH} />
      </Product>

      <Product>
        <H4>Remote health individuals versions</H4>
        <PolicyList policies={policiesIndividualsRH} />
      </Product>

      <Product>
        <H4>Nomad Health versions</H4>
        <PolicyList policies={policiesNH} />
      </Product>

      <Product>
        <H4>Archived policies</H4>
        <Accordion title="Nomad Insurance" isExpanded={false}>
          <PolicyList policies={archiveNI} />
        </Accordion>
        <Accordion title="Nomad Insurance Complete" isExpanded={false}>
          <PolicyList policies={archiveNIC} />
        </Accordion>
        <Accordion title="Nomad Health" isExpanded={false}>
          <PolicyList policies={archiveNH} />
        </Accordion>
        <Accordion title="Remote Health Community" isExpanded={false}>
          <PolicyList policies={archiveCommunityRH} />
        </Accordion>
        <Accordion title="Remote Health Affinity" isExpanded={false}>
          <PolicyList policies={archiveAffinityRH} />
        </Accordion>
        <Accordion title="Remote Health Individuals" isExpanded={false}>
          <PolicyList policies={archiveIndividualsRH} />
        </Accordion>
      </Product>
    </PoliciesContainer>
  );
};

export default Policies;
